/**
 * WeekGrid — Weekly schedule grid (rows = staff, cols = days)
 */

import { Fragment, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import type { ScheduleEntry, AvailabilityEntry, ShiftType } from '../../stores/schedule-store';
import { useBijrijderAssignments } from '../../stores/schedule-store';
import type { Staff, TimeEntry, LeaveEntry, WerkroosterDay } from '../../stores/workforce-store';
import { useWorkforceStore } from '../../stores';
import type { RouteListItem } from '../../lib/salesforce/types';
import { isHoliday } from '../../lib/dutch-holidays';
import { breakForRange } from '../../lib/hours-calculator';
import { vensterLabel, toHHmm as vensterHHmm } from '../../lib/dagdelen';
import { OpenDienstenRow } from './OpenDienstenRow';
import { VolumeRow } from './VolumeRow';
import { isAvondShift, isAvondOnlyDay, shiftChipClass } from './shift-style';

interface BijrijderAssignmentForCell {
  staff: Staff;
  routeId: string;
  routeName: string;
  chauffeurName: string | null;
}

interface WeekGridProps {
  staff: Staff[];
  schedule: ScheduleEntry[];
  availability: AvailabilityEntry[];
  timeEntries?: TimeEntry[];
  leaveEntries?: LeaveEntry[];
  weekDates: string[];
  teamFilter: 'all' | ShiftType;
  editMode: 'rooster' | 'beschikbaarheid';
  onCellClick: (staffId: string, date: string) => void;
  onShiftClick: (shift: ScheduleEntry) => void;
  openRoutes?: RouteListItem[];
  allRoutes?: RouteListItem[];
  onOpenRouteClick?: (route: RouteListItem) => void;
  onAssignedRouteClick?: (route: RouteListItem, staffId: string) => void;
  onOpenShiftClick?: (shift: ScheduleEntry) => void;
  onOpenCellClick?: (date: string) => void;
}

const contractLabels: Record<string, string> = {
  vast: 'vast',
  'min-max': 'min-max',
  '0-uren': '0-uren',
  student: 'student',
  zzp: 'zzp',
};

// ── Section grouping config ──────────────────────────────────────
type SectionKey = 'chauffeurs' | 'bijrijders' | 'wasserij' | 'kantoor' | 'beschikbaar' | 'afwezig';

const sectionConfig: Record<SectionKey, { label: string; bgClass: string; textClass: string }> = {
  chauffeurs:  { label: 'Chauffeurs',  bgClass: 'bg-emerald-50', textClass: 'text-emerald-700' },
  bijrijders:  { label: 'Bijrijders',  bgClass: 'bg-amber-50',   textClass: 'text-amber-700' },
  wasserij:    { label: 'Wasserij',    bgClass: 'bg-blue-50',    textClass: 'text-blue-700' },
  kantoor:     { label: 'Kantoor',     bgClass: 'bg-purple-50',  textClass: 'text-purple-700' },
  beschikbaar: { label: 'Beschikbaar', bgClass: 'bg-slate-50',   textClass: 'text-slate-600' },
  afwezig:     { label: 'Afwezig',     bgClass: 'bg-red-50',     textClass: 'text-red-600' },
};


function getWerkroosterForDate(staff: Staff, dateStr: string): WerkroosterDay | null {
  if (!staff.werkrooster) return null;
  const d = new Date(dateStr + 'T00:00:00');
  const dayOfWeek = d.getDay(); // 0=zo, 1=ma, ...
  return staff.werkrooster.find(w => w.day === dayOfWeek) || null;
}

function getAvailability(
  availability: AvailabilityEntry[],
  staffId: string,
  date: string
): AvailabilityEntry | undefined {
  return availability.find(a => a.staff_id === staffId && a.date === date);
}

function getShifts(
  schedule: ScheduleEntry[],
  staffId: string,
  date: string
): ScheduleEntry[] {
  return schedule.filter(s => s.staff_id === staffId && s.date === date);
}

// Pauze via de canonieke policy (src/lib/break-policy.ts) — dezelfde
// regels als klok-uit en de verwacht-berekening.
function getBreakMinutes(shiftType: string | null, timeFrom: string | null, timeTo: string | null): number {
  return breakForRange(shiftType, timeFrom, timeTo).breakMinutes;
}

// Extract HH:mm from ISO datetime or time string
/** Start HH:mm plus verwachte minuten → HH:mm (levende route). */
function verwachtTot(from: string, minuten: number): string {
  const delen = from.split(':').map((x) => parseInt(x, 10));
  const h = delen[0] ?? NaN;
  const m = delen[1] ?? NaN;
  if (!Number.isFinite(h) || !Number.isFinite(m)) return '';
  const tot = h * 60 + m + minuten;
  return `${String(Math.floor(tot / 60) % 24).padStart(2, '0')}:${String(tot % 60).padStart(2, '0')}`;
}

function toHHmm(val: string | null | undefined): string | null {
  if (!val) return null;
  // If ISO datetime like "2026-03-20T06:15:00Z" or "2026-03-20T06:15:00"
  if (val.includes('T')) {
    const d = new Date(val);
    if (isNaN(d.getTime())) return null;
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }
  // If already HH:mm:ss or HH:mm
  return val.slice(0, 5);
}

function getLeaveForCell(leaveEntries: LeaveEntry[] | undefined, staffId: string, date: string): LeaveEntry[] {
  if (!leaveEntries) return [];
  return leaveEntries.filter(l => l.staff_id === staffId && l.date === date);
}

const leaveColors: Record<string, string> = {
  vakantie: 'bg-amber-100 text-amber-700',
  ziekte: 'bg-red-100 text-red-700',
  bijzonder: 'bg-blue-100 text-blue-700',
  onbetaald: 'bg-slate-100 text-slate-600',
  feestdag: 'bg-purple-100 text-purple-700',
  verjaardag: 'bg-pink-100 text-pink-700',
};

const leaveLabels: Record<string, string> = {
  vakantie: 'Vakantie',
  ziekte: 'Ziek',
  bijzonder: 'Bijz. verlof',
  onbetaald: 'Onbetaald',
  feestdag: 'Feestdag',
  verjaardag: 'Verjaardag',
};

export function WeekGrid({
  staff,
  schedule,
  availability,
  timeEntries,
  leaveEntries,
  weekDates,
  teamFilter,
  editMode,
  onCellClick,
  onShiftClick,
  openRoutes,
  allRoutes,
  onOpenRouteClick,
  onAssignedRouteClick,
  onOpenShiftClick,
  onOpenCellClick,
}: WeekGridProps) {
  const { sickLeaves } = useWorkforceStore();
  const sickStaffIds = useMemo(() => new Set(
    sickLeaves.filter(sl => !sl.end_date).map(sl => sl.staff_id)
  ), [sickLeaves]);
  const navigate = useNavigate();
  const bijrijderAssignments = useBijrijderAssignments();

  const filteredStaff = useMemo(() => {
    if (teamFilter === 'all') return staff.filter(s => s.is_active);
    return staff.filter(s => s.is_active && s.team === teamFilter);
  }, [staff, teamFilter]);

  // ── Shift-type sections: group shifts by type + date (for rooster mode) ──
  const staffById = useMemo(() => new Map(filteredStaff.map(s => [s.id, s])), [filteredStaff]);

  // Bijrijder section: per-date list of (staff, route) tuples, scoped to filteredStaff & weekDates
  const bijrijderSection = useMemo(() => {
    const byDate = new Map<string, BijrijderAssignmentForCell[]>();
    for (const date of weekDates) byDate.set(date, []);

    const uniqueStaffIds = new Set<string>();
    for (const assignment of bijrijderAssignments) {
      if (!byDate.has(assignment.date)) continue;
      const s = staffById.get(assignment.staffId);
      if (!s) continue;
      byDate.get(assignment.date)!.push({
        staff: s,
        routeId: assignment.routeId,
        routeName: assignment.routeName,
        chauffeurName: assignment.chauffeurName,
      });
      uniqueStaffIds.add(s.id);
    }
    return { byDate, count: uniqueStaffIds.size };
  }, [bijrijderAssignments, staffById, weekDates]);

  const shiftSectionDefs = useMemo(() => {
    const defs: Array<{ shiftType: string; sectionKey: SectionKey }> = [
      { shiftType: 'route', sectionKey: 'chauffeurs' },
      { shiftType: 'wasserij', sectionKey: 'wasserij' },
      { shiftType: 'kantoor', sectionKey: 'kantoor' },
    ];
    return defs.map(({ shiftType, sectionKey }) => {
      const byDate = new Map<string, Array<{ shift: ScheduleEntry; staff: Staff }>>();
      for (const date of weekDates) byDate.set(date, []);

      for (const entry of schedule) {
        if (!entry.staff_id || entry.shift_type !== shiftType) continue;
        const s = staffById.get(entry.staff_id);
        if (!s) continue;
        byDate.get(entry.date)?.push({ shift: entry, staff: s });
      }

      const uniqueIds = new Set<string>();
      const avondIds = new Set<string>();
      for (const arr of byDate.values()) {
        arr.sort((a, b) => (a.shift.time_from || '').localeCompare(b.shift.time_from || ''));
        for (const e of arr) {
          uniqueIds.add(e.staff.id);
          if (isAvondShift(e.shift.time_from)) avondIds.add(e.staff.id);
        }
      }

      return { sectionKey, shiftType, byDate, count: uniqueIds.size, avondCount: avondIds.size };
    });
  }, [schedule, staffById, weekDates]);

  // ── Unscheduled staff: everyone who has at least one day without a shift ──
  // People with shifts on some days still appear here for their free days
  const shiftDaySet = useMemo(() => new Set(
    schedule.filter(s => s.staff_id && s.shift_type).map(s => `${s.staff_id}:${s.date}`)
  ), [schedule]);

  // Iedereen met minstens één vrije dag hoort hier, ook wie deels is ingepland.
  // Bewust géén filter op "heeft al een beschikbaarheids- of werkroosterregel":
  // juist de lege cel is de vraag die we willen zien staan ("is Dinand vrijdag
  // beschikbaar?"). Op die eis filteren verbergt precies de dagen zonder antwoord.
  const unscheduledStaff = useMemo(() => {
    return filteredStaff
      .filter(member => weekDates.some(d => !shiftDaySet.has(`${member.id}:${d}`)))
      .sort((a, b) => a.name.localeCompare(b.name, 'nl'));
  }, [filteredStaff, shiftDaySet, weekDates]);

  // Count scheduled hours per staff for this week
  // Uses actual clock times when available, otherwise planned/default
  const weeklyHours = useMemo(() => {
    const hours = new Map<string, number>();
    for (const entry of schedule) {
      if (!entry.time_from || !entry.time_to || !entry.staff_id) continue;

      // Check for actual clock times on route shifts
      let fromStr = entry.time_from;
      let toStr = entry.time_to;
      if (entry.salesforce_route_id && timeEntries) {
        const te = timeEntries.find(t => t.staff_id === entry.staff_id && t.date === entry.date && t.clock_out);
        if (te) {
          const actualIn = toHHmm(te.clock_in);
          const actualOut = toHHmm(te.clock_out);
          if (actualIn && actualOut) {
            fromStr = actualIn;
            toStr = actualOut;
          }
        }
      }

      const parts = fromStr.split(':').map(Number);
      const parts2 = toStr.split(':').map(Number);
      const bruto = ((parts2[0] || 0) + (parts2[1] || 0) / 60) - ((parts[0] || 0) + (parts[1] || 0) / 60);
      const breakMins = getBreakMinutes(entry.shift_type, entry.time_from, entry.time_to);
      const netto = bruto - breakMins / 60;
      hours.set(entry.staff_id, (hours.get(entry.staff_id) || 0) + netto);
    }
    return hours;
  }, [schedule, timeEntries]);

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse table-fixed">
        <colgroup>
          <col className="w-[180px]" />
          {weekDates.map(date => (
            <col key={date} style={{ width: `${(100 - 18) / weekDates.length}%`, minWidth: '130px' }} />
          ))}
          <col className="w-[70px]" />
        </colgroup>
        <thead>
          <tr>
            <th className="sticky left-0 z-10 bg-white w-[180px] min-w-[180px] p-3 text-left text-xs font-medium text-muted uppercase border-b border-gray-200">
              {editMode === 'beschikbaarheid' ? 'Medewerker' : ''}
            </th>
            {weekDates.map(date => {
              const d = parseISO(date);
              const isToday = date === new Date().toISOString().split('T')[0];
              const holiday = isHoliday(date);
              return (
                <th
                  key={date}
                  className={clsx(
                    'min-w-[130px] p-3 text-center text-xs font-medium uppercase border-b border-gray-200',
                    holiday ? 'bg-red-50 text-red-700' : isToday ? 'bg-indigo-50 text-indigo-700' : 'text-muted'
                  )}
                >
                  <div>{format(d, 'EEE', { locale: nl })}</div>
                  <div className="text-sm font-semibold">{format(d, 'd MMM', { locale: nl })}</div>
                  {holiday && <div className="text-[9px] font-normal normal-case text-red-600">{holiday.name}</div>}
                </th>
              );
            })}
            <th className="w-[70px] min-w-[70px] p-3 text-center text-xs font-medium text-muted uppercase border-b border-gray-200">
              Uren
            </th>
          </tr>
        </thead>
        <tbody>
          {editMode === 'rooster' && allRoutes && allRoutes.length > 0 && (
            <VolumeRow routes={allRoutes} weekDates={weekDates} />
          )}
          {editMode === 'rooster' && onOpenRouteClick && onOpenCellClick && (
            <OpenDienstenRow
              routes={openRoutes || []}
              openShifts={schedule.filter(s => !s.staff_id)}
              weekDates={weekDates}
              onRouteClick={onOpenRouteClick}
              onShiftClick={onOpenShiftClick || (() => {})}
              onCellClick={onOpenCellClick}
            />
          )}

          {/* ── Shift-type sections (rooster mode) or person rows (beschikbaarheid mode) ── */}
          {editMode === 'rooster' ? (
            <>
              {/* Shift sections: one row per type, shifts stacked in day cells */}
              {shiftSectionDefs.map(({ sectionKey, byDate, count, avondCount }) => {
                if (count === 0) return null;
                const config = sectionConfig[sectionKey];
                const colSpan = weekDates.length + 2;

                return (
                  <Fragment key={sectionKey}>
                    <tr>
                      <td colSpan={colSpan} className={clsx(config.bgClass, 'px-4 py-1.5 border-b border-gray-200')}>
                        <div className="flex items-center gap-2">
                          <span className={clsx('text-xs font-semibold uppercase tracking-wide', config.textClass)}>
                            {config.label}
                          </span>
                          <span className={clsx('text-xs font-medium opacity-50', config.textClass)}>
                            {count}
                          </span>
                          {avondCount > 0 && (
                            <span className="text-xs font-medium text-orange-600">
                              · {avondCount} avond
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                    <tr className="border-b border-gray-200">
                      <td className="sticky left-0 z-10 bg-white border-r border-gray-100" />
                      {weekDates.map(date => {
                        const entries = byDate.get(date) || [];
                        const holiday = isHoliday(date);
                        const isToday = date === new Date().toISOString().split('T')[0];
                        return (
                          <td
                            key={date}
                            className={clsx(
                              'p-1.5 border-r border-gray-100 align-top transition-colors',
                              holiday && 'bg-red-50/40',
                              isToday && !holiday && 'bg-indigo-50/30',
                            )}
                          >
                            {entries.map(({ shift, staff: staffMember }) => {
                              const linkedRoute = shift.salesforce_route_id && allRoutes
                                ? allRoutes.find(r => r.id === shift.salesforce_route_id)
                                : null;
                              // Route shifts: show route name. Other shifts: omit label (section header is enough)
                              const label = linkedRoute
                                ? (linkedRoute.routeName || linkedRoute.name)
                                : null;

                              const staffTimeEntry = linkedRoute && timeEntries
                                ? timeEntries.find(te => te.staff_id === staffMember.id && te.date === date && te.clock_out)
                                : null;
                              const actualIn = toHHmm(staffTimeEntry?.clock_in);
                              const actualOut = toHHmm(staffTimeEntry?.clock_out);
                              const hasActual = actualIn && actualOut;
                              const isLocked = linkedRoute?.locked;
                              const plannedFrom = shift.time_from?.slice(0, 5);
                              const plannedTo = shift.time_to?.slice(0, 5);
                              const isRouteShift = !!linkedRoute;
                              const phase: 'actual' | 'planned' | 'default' =
                                hasActual ? 'actual' : isLocked ? 'planned' : 'default';

                              return (
                                <button
                                  key={shift.id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (linkedRoute && onAssignedRouteClick) {
                                      onAssignedRouteClick(linkedRoute, staffMember.id);
                                    } else {
                                      onShiftClick(shift);
                                    }
                                  }}
                                  className={clsx(
                                    'w-full rounded-lg border p-1.5 text-left transition-all hover:shadow-sm mb-1 last:mb-0',
                                    shiftChipClass(shift.shift_type, shift.time_from)
                                  )}
                                >
                                  <div className="text-xs font-semibold truncate">
                                    {staffMember.name.split(' ')[0]}{label ? ` · ${label}` : ''}
                                  </div>
                                  {isRouteShift && hasActual ? (
                                    <>
                                      <div className="text-[10px] font-medium">
                                        {actualIn}-{actualOut}
                                      </div>
                                      {isLocked && (plannedFrom !== actualIn || plannedTo !== actualOut) && (
                                        <div className="text-[9px] opacity-50 line-through">
                                          plan {plannedFrom}-{plannedTo}
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <div className={clsx(
                                      'text-[10px]',
                                      isRouteShift && phase === 'default' && 'opacity-50 italic',
                                      isRouteShift && phase === 'planned' && 'opacity-75',
                                    )}>
                                      {phase === 'default' && '~'}{plannedFrom}-{plannedTo}
                                      {isRouteShift && linkedRoute?.levendStand === 'actueel' && linkedRoute.verwachteDuurMinuten != null && plannedFrom && (
                                        <span className="ml-1 opacity-70" title="Verwacht einde: geplande start plus de gemeten plan/klok-factor van deze route">
                                          · verwacht tot {verwachtTot(plannedFrom, linkedRoute.verwachteDuurMinuten)}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                          </td>
                        );
                      })}
                      <td className="p-3" />
                    </tr>
                  </Fragment>
                );
              })}

              {/* Bijrijders: virtual section sourced from Routes__c.Bijrijder__c */}
              {bijrijderSection.count > 0 && (
                <Fragment key="bijrijders-section">
                  <tr>
                    <td colSpan={weekDates.length + 2} className={clsx(sectionConfig.bijrijders.bgClass, 'px-4 py-1.5 border-b border-gray-200')}>
                      <div className="flex items-center gap-2">
                        <span className={clsx('text-xs font-semibold uppercase tracking-wide', sectionConfig.bijrijders.textClass)}>
                          {sectionConfig.bijrijders.label}
                        </span>
                        <span className={clsx('text-xs font-medium opacity-50', sectionConfig.bijrijders.textClass)}>
                          {bijrijderSection.count}
                        </span>
                      </div>
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="sticky left-0 z-10 bg-white border-r border-gray-100" />
                    {weekDates.map(date => {
                      const entries = bijrijderSection.byDate.get(date) || [];
                      const holiday = isHoliday(date);
                      const isToday = date === new Date().toISOString().split('T')[0];
                      return (
                        <td
                          key={date}
                          className={clsx(
                            'p-1.5 border-r border-gray-100 align-top',
                            holiday && 'bg-red-50/40',
                            isToday && !holiday && 'bg-indigo-50/30',
                          )}
                        >
                          {entries.map(entry => (
                            <button
                              key={`${entry.routeId}-${entry.staff.id}`}
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/route-planner?routeId=${entry.routeId}`);
                              }}
                              className="w-full rounded-lg border border-amber-200 bg-amber-50 text-amber-900 p-1.5 text-left transition-all hover:shadow-sm hover:bg-amber-100 mb-1 last:mb-0"
                              title={entry.chauffeurName ? `Bijrijder · route ${entry.routeName} · met ${entry.chauffeurName}` : `Bijrijder · route ${entry.routeName}`}
                            >
                              <div className="text-xs font-semibold truncate flex items-center gap-1">
                                <span aria-hidden>👥</span>
                                {entry.staff.name.split(' ')[0]} · {entry.routeName}
                              </div>
                              {entry.chauffeurName && (
                                <div className="text-[10px] opacity-75 truncate">
                                  met {entry.chauffeurName.split(' ')[0]}
                                </div>
                              )}
                            </button>
                          ))}
                        </td>
                      );
                    })}
                    <td className="p-3" />
                  </tr>
                </Fragment>
              )}

              {/* Beschikbaarheid: iedereen met een vrije dag, ook wie deels is ingepland */}
              {unscheduledStaff.length > 0 && (
                <Fragment>
                  <tr>
                    <td colSpan={weekDates.length + 2} className={clsx(sectionConfig.beschikbaar.bgClass, 'px-4 py-1.5 border-b border-gray-200')}>
                      <div className="flex items-center gap-2">
                        <span className={clsx('text-xs font-semibold uppercase tracking-wide', sectionConfig.beschikbaar.textClass)}>
                          Beschikbaarheid
                        </span>
                        <span className={clsx('text-xs font-medium opacity-50', sectionConfig.beschikbaar.textClass)}>
                          {unscheduledStaff.length}
                        </span>
                      </div>
                    </td>
                  </tr>
                  {unscheduledStaff.map(member => {
                    const isSick = sickStaffIds.has(member.id);
                    return (
                      <tr key={member.id} className="border-b border-gray-100 hover:bg-gray-50/50">
                        <td className="sticky left-0 z-10 bg-white p-3 border-r border-gray-100">
                          <div className="flex items-center gap-2">
                            <div className={clsx(
                              'w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0',
                              isSick ? 'bg-red-50 text-red-400' : 'bg-slate-100 text-slate-600',
                            )}>
                              {member.name.charAt(0)}
                            </div>
                            <div className="min-w-0">
                              <p className={clsx('text-sm font-medium truncate', isSick ? 'text-muted' : 'text-main')}>{member.name.split(' ')[0]}</p>
                              <p className="text-[10px] text-muted">{isSick ? 'ziek' : contractLabels[member.contract_type] || member.contract_type}</p>
                            </div>
                          </div>
                        </td>
                        {weekDates.map(date => {
                          const dayShifts = getShifts(schedule, member.id, date);
                          const hasShift = dayShifts.length > 0;
                          const avondOnly = isAvondOnlyDay(dayShifts);
                          const avail = getAvailability(availability, member.id, date);
                          const leaves = getLeaveForCell(leaveEntries, member.id, date);
                          const roosterDay = getWerkroosterForDate(member, date);
                          const hasLeave = leaves.length > 0;
                          const holiday = isHoliday(date);
                          const isToday = date === new Date().toISOString().split('T')[0];
                          const hasAvail = !isSick && !hasShift && (avail !== undefined || (!!roosterDay && !hasLeave));
                          const isAvail = isSick ? false : (avail?.is_available ?? (!!roosterDay && !hasLeave ? true : null));
                          return (
                            <td
                              key={date}
                              onClick={() => !hasShift ? onCellClick(member.id, date) : undefined}
                              className={clsx(
                                'p-1.5 border-r border-gray-100 align-top transition-colors',
                                holiday && 'bg-red-50/40',
                                isToday && !holiday && 'bg-indigo-50/30',
                                hasShift && !avondOnly && 'bg-slate-50/50',
                                avondOnly && 'bg-orange-50/40',
                                !hasShift && !isSick && 'cursor-pointer',
                                !hasShift && !isSick && hasAvail && isAvail && 'bg-emerald-50/50 hover:bg-emerald-100',
                                !hasShift && !isSick && hasAvail && !isAvail && 'bg-red-50/40 hover:bg-red-100',
                                !hasShift && !hasAvail && !isSick && 'hover:bg-slate-100',
                              )}
                            >
                              {hasShift ? (
                                <div className={clsx(
                                  'text-[10px] text-center py-2',
                                  avondOnly ? 'text-orange-500 font-medium' : 'text-slate-300',
                                )}>
                                  {avondOnly ? 'avond' : 'ingepland'}
                                </div>
                              ) : isSick ? (
                                <div className="text-[10px] text-center py-2 text-red-400">ziek</div>
                              ) : hasLeave ? leaves.map(le => (
                                <div key={le.id} className={clsx('rounded px-1.5 py-0.5 text-[10px] font-medium mb-0.5', leaveColors[le.leave_type] || 'bg-gray-100 text-gray-600')}>
                                  {leaveLabels[le.leave_type] || le.leave_type}
                                </div>
                              )) : hasAvail ? (
                                <div className={clsx('text-[10px] text-center py-2', isAvail ? 'text-emerald-500' : 'text-red-400')}>
                                  {isAvail
                                    ? (vensterLabel(avail?.time_from ?? null, avail?.time_to ?? null)?.toLowerCase() ?? 'beschikbaar')
                                    : 'niet besch.'}
                                </div>
                              ) : null}
                            </td>
                          );
                        })}
                        <td className="p-3 text-center text-sm font-medium text-muted">
                          {(weeklyHours.get(member.id) || 0) > 0
                            ? `${weeklyHours.get(member.id)!.toFixed(0)}u`
                            : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </Fragment>
              )}
            </>
          ) : (
            /* ── Beschikbaarheid mode: all staff as person rows ── */
            filteredStaff.map(member => {
              const avails = weekDates.map(d => getAvailability(availability, member.id, d));
              return (
                <tr key={member.id} className="border-b border-gray-100 hover:bg-gray-50/50">
                  <td className="sticky left-0 z-10 bg-white p-3 border-r border-gray-100">
                    <div className="flex items-center gap-2">
                      <div className={clsx(
                        'w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0',
                        member.team === 'wasserij' && 'bg-blue-100 text-blue-700',
                        member.team === 'route' && 'bg-emerald-100 text-emerald-700',
                        member.team === 'kantoor' && 'bg-purple-100 text-purple-700',
                        (!member.team || member.team === 'algemeen') && 'bg-slate-100 text-slate-600',
                      )}>
                        {member.name.charAt(0)}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-main truncate">{member.name.split(' ')[0]}</p>
                        <p className="text-[10px] text-muted">{contractLabels[member.contract_type] || member.contract_type}</p>
                      </div>
                    </div>
                  </td>
                  {weekDates.map((date, i) => {
                    const avail = avails[i];
                    const leaves = getLeaveForCell(leaveEntries, member.id, date);
                    const roosterDay = getWerkroosterForDate(member, date);
                    const hasLeave = leaves.length > 0;
                    const holiday = isHoliday(date);
                    const isToday = date === new Date().toISOString().split('T')[0];
                    const isSick = sickStaffIds.has(member.id);
                    const hasAvail = !isSick && (avail !== undefined || (!!roosterDay && !hasLeave));
                    const isAvail = isSick ? false : (avail?.is_available ?? (!!roosterDay && !hasLeave ? true : null));
                    return (
                      <td
                        key={date}
                        onClick={() => onCellClick(member.id, date)}
                        className={clsx(
                          'p-1.5 border-r border-gray-100 align-top cursor-pointer transition-colors',
                          holiday && 'bg-red-50/40',
                          isToday && !holiday && 'bg-indigo-50/30',
                          hasAvail && isAvail && 'bg-emerald-100 hover:bg-emerald-200',
                          hasAvail && !isAvail && 'bg-red-100 hover:bg-red-200',
                          !hasAvail && 'hover:bg-slate-100',
                        )}
                      >
                        <div className={clsx(
                          'text-center py-2 rounded-lg text-xs font-medium select-none',
                          hasLeave && 'text-amber-600',
                          !hasLeave && hasAvail && isAvail && 'text-emerald-700',
                          !hasLeave && hasAvail && !isAvail && 'text-red-600',
                          !hasLeave && !hasAvail && 'text-slate-300',
                        )}>
                          {hasLeave ? 'Verlof' : hasAvail
                            ? (isAvail
                                ? (vensterLabel(avail?.time_from ?? null, avail?.time_to ?? null) ?? 'Beschikbaar')
                                : 'Niet besch.')
                            : 'Onbekend'}
                          {/* Het opgegeven venster wint van het werkrooster: dat is
                              wat de medewerker zelf zei, het werkrooster is de aanname. */}
                          {!hasLeave && (avail?.time_from || avail?.time_to) ? (
                            <div className="text-[10px] opacity-75 mt-0.5">
                              {vensterHHmm(avail.time_from) ?? '…'}-{vensterHHmm(avail.time_to) ?? '…'}
                            </div>
                          ) : !hasLeave && roosterDay ? (
                            <div className="text-[10px] opacity-75 mt-0.5">{roosterDay.from}-{roosterDay.to}</div>
                          ) : null}
                        </div>
                      </td>
                    );
                  })}
                  <td className="p-3 text-center text-sm font-medium text-main">
                    {(weeklyHours.get(member.id) || 0) > 0 ? `${weeklyHours.get(member.id)!.toFixed(0)}u` : '-'}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
